import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  Alert,
  FlexCenter,
  FlexColumn,
  LineageGraph,
} from '@elementary/common-ui/components';
import {
  getDagDepthBoundaryFromBoundaries,
  mapGqlLineageToApiLineage,
} from '@elementary/common-ui/components/lineageGraph';
import type { HopLimit } from '@elementary/common-ui/components/lineageGraph/types';
import { space } from '@elementary/common-ui/style/utils';
import { LINEAGE_GROUPING_STRATEGY } from '../../constants';
import useIncidentLineage from '../../hooks/useIncidentLineage';
import type { IncidentDetail, IncidentLineageData } from '../../types';

const TabContent = styled(FlexColumn)`
  gap: ${space('xxl')};
  padding: ${space('lg', 0)};
  flex: 1;
  min-height: 0;
`;

const GraphWrap = styled(Box)`
  flex: 1;
  min-height: 320px;
  width: 100%;
  position: relative;
`;

const GraphLoadingOverlay = styled(FlexCenter)`
  position: absolute;
  inset: 0;
  z-index: 5;
  background: ${({ theme }) => theme.palette.surface.base}aa;
  pointer-events: none;
`;

const DEFAULT_LINEAGE_HOP_LIMIT: HopLimit = { upstream: 1, downstream: 1 };
const LINEAGE_GROUPED_HOP_LIMIT: HopLimit = { upstream: 0, downstream: 0 };

export const getDefaultIncidentLineageHopLimit = (
  groupingStrategy: string,
): HopLimit =>
  groupingStrategy === LINEAGE_GROUPING_STRATEGY
    ? LINEAGE_GROUPED_HOP_LIMIT
    : DEFAULT_LINEAGE_HOP_LIMIT;

const EMPTY_LINEAGE_DATA: IncidentLineageData = {
  rootAssetIds: [],
  rootColumnIds: [],
  assetLineage: {
    nodes: [],
    edges: [],
    boundaries: { upstream: 0, downstream: 0 },
  },
  columnLineage: {
    nodes: [],
    edges: [],
    boundaries: { upstream: 0, downstream: 0 },
  },
};

type Props = {
  incident: IncidentDetail;
};

const IncidentLineageTab = ({ incident }: Props) => {
  const [hopLimit, setHopLimit] = useState<HopLimit>(() =>
    getDefaultIncidentLineageHopLimit(incident.groupingStrategy),
  );

  useEffect(() => {
    setHopLimit(getDefaultIncidentLineageHopLimit(incident.groupingStrategy));
  }, [incident.id, incident.groupingStrategy]);

  const { lineage, isLoading, isFetching, error } = useIncidentLineage(
    incident.id,
    hopLimit,
  );

  const { assetLineage, columnLineage } = lineage ?? EMPTY_LINEAGE_DATA;

  const hasLineageData =
    !!lineage &&
    (assetLineage.nodes.length > 0 ||
      assetLineage.edges.length > 0 ||
      (columnLineage?.nodes.length ?? 0) > 0 ||
      (columnLineage?.edges.length ?? 0) > 0);

  const rootAssetIds = lineage?.rootAssetIds ?? [];
  const rootColumnIds = lineage?.rootColumnIds ?? [];

  const apiLineage = useMemo(
    () =>
      lineage
        ? mapGqlLineageToApiLineage({
            assets: assetLineage,
            columns: columnLineage,
          })
        : null,
    [assetLineage, columnLineage, lineage],
  );

  const dagDepthBoundary =
    lineage &&
    getDagDepthBoundaryFromBoundaries(
      assetLineage.boundaries,
      columnLineage?.boundaries,
    );

  // Cap the requested hop limit to the real boundary so the hops control reflects
  // what's available (e.g. shows 0 when nothing exists in that direction).
  const displayHopLimit: HopLimit = dagDepthBoundary
    ? {
        upstream: Math.min(
          hopLimit.upstream ?? 0,
          dagDepthBoundary.incomingDepth,
        ),
        downstream: Math.min(
          hopLimit.downstream ?? 0,
          dagDepthBoundary.outgoingDepth,
        ),
      }
    : hopLimit;

  const showInitialLoader = !lineage && (isLoading || isFetching);
  const showRefetchOverlay = !!lineage && isFetching;
  const hasError = error != null;

  return (
    <TabContent>
      {hasError && (
        <Alert severity="error">
          Could not load lineage for this incident.
        </Alert>
      )}

      {showInitialLoader && (
        <GraphWrap>
          <FlexCenter sx={{ height: '100%', minHeight: 200 }}>
            <CircularProgress />
          </FlexCenter>
        </GraphWrap>
      )}

      {!showInitialLoader && hasLineageData && apiLineage && dagDepthBoundary && (
        <GraphWrap>
          {showRefetchOverlay && (
            <GraphLoadingOverlay>
              <CircularProgress size={32} />
            </GraphLoadingOverlay>
          )}
          <LineageGraph
            isControlled
            overrideAssetIds={rootAssetIds}
            overrideColumnIds={rootColumnIds}
            overrideLineageData={apiLineage}
            overrideHopLimit={displayHopLimit}
            overrideDagDepthBoundary={dagDepthBoundary}
            overrideColorBy="testStatus"
            onHopLimitChange={setHopLimit}
            showOverlayControls
            legendSelectedLabel="Monitored"
          />
        </GraphWrap>
      )}

      {!showInitialLoader && !hasError && !hasLineageData && (
        <Typography sx={{ color: 'content.tertiary' }}>
          No lineage data available for this incident.
        </Typography>
      )}
    </TabContent>
  );
};

export default IncidentLineageTab;
